import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf, Lock } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";

const AdminLogin = () => {
  const { login, user, checked } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (checked && user) navigate("/admin", { replace: true });
  }, [checked, user, navigate]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(email, password);
      toast.success("Bienvenido de vuelta, curador.");
      navigate("/admin", { replace: true });
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "No se pudo iniciar sesión.";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white aura relative overflow-hidden px-4">
      <div className="absolute inset-0 bio-grid opacity-30" />
      <div className="relative z-10 w-full max-w-md rounded-3xl border border-emerald-500/20 bg-slate-900/60 glass p-8">
        <div className="flex items-center gap-2 mb-8">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-emerald-500 to-cyan-500 text-slate-950">
            <Leaf className="h-5 w-5" strokeWidth={2.4} />
          </span>
          <div>
            <div className="font-display font-extrabold">ZeroToxic<span className="text-emerald-400">Market</span></div>
            <div className="font-mono-tech text-[10px] uppercase tracking-widest text-emerald-500/80">Panel del Curador</div>
          </div>
        </div>

        <h1 className="font-display text-2xl font-bold flex items-center gap-2"><Lock className="h-5 w-5 text-emerald-400" /> Iniciar sesión</h1>
        <p className="mt-1 text-sm text-slate-400">Acceso exclusivo para administradores.</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <label className="text-xs font-mono-tech uppercase tracking-widest text-emerald-400">Email</label>
            <input
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 h-11 w-full rounded-lg border border-slate-700 bg-slate-950/50 px-3 text-sm outline-none focus:border-emerald-500/60"
              placeholder="admin@zerotoxic.com"
            />
          </div>
          <div>
            <label className="text-xs font-mono-tech uppercase tracking-widest text-emerald-400">Contraseña</label>
            <input
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 h-11 w-full rounded-lg border border-slate-700 bg-slate-950/50 px-3 text-sm outline-none focus:border-emerald-500/60"
              placeholder="••••••••"
            />
          </div>
          {error && <p data-testid="login-error" className="text-sm text-red-400">{error}</p>}
          <Button
            data-testid="admin-login-btn"
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-gradient-to-r from-emerald-500 to-cyan-500 hover:from-emerald-400 hover:to-cyan-400 text-slate-950 font-bold"
          >
            {loading ? "Ingresando…" : "Ingresar"}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
